/**
 * TopBar Component - Application header with user menu
 *
 * Version History:
 * v4.34 - 2026-10-28 - WEBSITE-DISABLE-01 (row 781): dark-mode toggle removed for customers (Branko); the portal is light-only.
 * v2.94 - 2026-02-07 - Added NotificationBell between dark mode toggle and user avatar
 * v2.93 - 2026-02-07 - Added hamburger menu toggle for mobile responsive
 * v2.92 - 2026-02-07 - Added dark mode toggle button
 * v2.55 - 2026-02-04 - Migrated to Clerk authentication (useUser, useClerk signOut)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles'; // v4.34 - WEBSITE-DISABLE-01: logo reads the MUI theme mode
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu'; // v2.93 - Hamburger menu
import NotificationBell from '../NotificationBell'; // v2.94 - Notification center
import MyLojicLogo from '../MyLojicLogo'; // v4.07 - Inline SVG logo

const TOPBAR_HEIGHT = 64;

// v2.93 - Added onMenuToggle prop for mobile hamburger
interface TopBarProps {
  onMenuToggle?: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const muiTheme = useTheme(); // v4.34 - WEBSITE-DISABLE-01 (row 781): ThemeContext toggle removed

  // Get user info from Clerk
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const userName = user?.firstName || (userEmail ? userEmail.split('@')[0] : 'User');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // v2.55 - Use Clerk's signOut method
  const handleLogout = async () => {
    handleMenuClose();
    await signOut();
    // Redirect to logged-out page after sign out
    navigate('/logged-out', { replace: true });
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/dashboard/config/settings');
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        height: TOPBAR_HEIGHT,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ height: TOPBAR_HEIGHT, minHeight: TOPBAR_HEIGHT }}>
        {/* v2.93 - Hamburger menu button (mobile only) */}
        {onMenuToggle && (
          <IconButton
            onClick={onMenuToggle}
            size="small"
            sx={{ mr: 1, color: 'text.secondary', display: { md: 'none' } }}
            aria-label="Toggle navigation menu"
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo - v3.89 - replaced text with logo image */}
        <Box
          component="div"
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => navigate('/dashboard')}
        >
          <MyLojicLogo isDark={muiTheme.palette.mode === 'dark'} height={36} />
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* v4.34 - WEBSITE-DISABLE-01 (row 781) - dark-mode toggle removed (Branko 2026-10-28); the portal is light-only. */}

        {/* v2.94 - Notification bell */}
        <NotificationBell />

        {/* User Menu */}
        <IconButton
          onClick={handleMenuOpen}
          size="small"
          aria-label="Open user menu"
          aria-controls={anchorEl ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={anchorEl ? 'true' : undefined}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'accent.main',
              color: 'accent.contrastText',
              fontWeight: 600,
            }}
          >
            {userName.charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1,
              minWidth: 200,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {userName}
            </Typography>
            {userEmail && (
              <Typography variant="caption" color="text.secondary">
                {userEmail}
              </Typography>
            )}
          </Box>
          <Divider />
          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export { TOPBAR_HEIGHT };
export default TopBar;
